import { useState } from "react";
import { Camera, ChevronDown, ChevronUp } from "lucide-react";
import Reveal from "./Reveal";

function BigPhoto({ src, date, testid }) {
  return (
    <div
      className="group relative overflow-hidden rounded-xl border border-white/5 hover:border-gold/30 transition-colors duration-500 h-full"
      data-testid={testid}
    >
      <img
        src={src}
        alt="Fotografie z realizace RESTART PRO"
        className="w-full h-64 sm:h-80 object-cover transition-transform duration-700 group-hover:scale-[1.03]"
      />
      {date && (
        <span className="absolute bottom-3 left-3 text-[11px] font-mono tracking-wide text-zinc-100 bg-black/70 rounded-md px-2.5 py-1">
          {date}
        </span>
      )}
    </div>
  );
}

function Album({ badge, title, desc, covers, photos, testid }) {
  const [open, setOpen] = useState(false);
  return (
    <div className="mt-14" data-testid={`${testid}-album`}>
      <Reveal>
        {badge}
        <h3 className={`font-display text-2xl sm:text-3xl text-zinc-100 ${badge ? "mt-4" : ""}`}>
          {title}
        </h3>
        {desc && <p className="mt-3 text-sm text-zinc-400 max-w-xl leading-relaxed">{desc}</p>}
      </Reveal>

      <div className={`mt-6 grid gap-5 ${covers.length > 1 ? "md:grid-cols-2" : "max-w-2xl"}`}>
        {covers.map((p, i) => (
          <Reveal key={p.src} delay={0.08 + i * 0.06} className="h-full">
            <BigPhoto src={p.src} date={p.date} testid={`${testid}-cover-${i}`} />
          </Reveal>
        ))}
      </div>

      {photos.length > 0 && (
        <Reveal delay={0.1}>
          <button
            type="button"
            onClick={() => setOpen((v) => !v)}
            data-testid={`${testid}-toggle`}
            aria-expanded={open}
            className="mt-5 inline-flex items-center gap-2 border border-gold/40 text-gold hover:bg-gold hover:text-black transition-colors duration-300 rounded-full px-6 py-2.5 text-sm font-semibold"
          >
            <Camera size={15} />
            {open ? "Skrýt fotky" : `Více foto z této realizace (${photos.length})`}
            {open ? <ChevronUp size={15} /> : <ChevronDown size={15} />}
          </button>
        </Reveal>
      )}

      {open && (
        <div className="mt-5 grid grid-cols-2 md:grid-cols-3 gap-5" data-testid={`${testid}-grid`}>
          {photos.map((p, i) => (
            <div
              key={p.src}
              className="group relative overflow-hidden rounded-xl border border-white/5 hover:border-gold/30 transition-colors duration-500"
              data-testid={`${testid}-photo-${i}`}
            >
              <img
                src={p.src}
                alt="Fotografie z realizace RESTART PRO"
                loading="lazy"
                className="w-full h-44 sm:h-52 object-cover transition-transform duration-700 group-hover:scale-105"
              />
              {p.date && (
                <span className="absolute bottom-2.5 left-2.5 text-[10px] font-mono tracking-wide text-zinc-100 bg-black/70 rounded-md px-2 py-0.5">
                  {p.date}
                </span>
              )}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

export default function Gallery() {
  return (
    <section id="projekty" data-testid="gallery-section" className="py-24 lg:py-32">
      <div className="max-w-7xl mx-auto px-4 sm:px-8 lg:px-16">
        <Reveal>
          <p className="text-xs font-mono tracking-[0.3em] uppercase text-gold/70 mb-4">
            Naše projekty
          </p>
          <h2 className="font-display text-3xl sm:text-4xl lg:text-5xl text-zinc-100 leading-tight">
            Práce, na kterou <span className="gold-text">jsme hrdí</span>
          </h2>
          <p className="mt-5 max-w-2xl text-zinc-400 leading-relaxed">
            Každé album je jedna reálná zakázka. Žádné katalogové fotky — jen naše práce,
            focená přímo na stavbách.
          </p>
        </Reveal>

        <Album
          testid="gallery-aktualni"
          badge={
            <span
              className="inline-flex items-center gap-2 text-[11px] font-mono tracking-[0.2em] uppercase bg-gold text-black font-semibold rounded-full px-4 py-1.5"
              data-testid="gallery-aktualni-badge"
            >
              <span className="relative flex h-2 w-2">
                <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-black opacity-60" />
                <span className="relative inline-flex rounded-full h-2 w-2 bg-black" />
              </span>
              Právě stavíme
            </span>
          }
          title={
            <>
              Aktuální projekt — <span className="gold-text">Zateplení fasády, Praha Kunratice</span>
            </>
          }
          desc="Rodinný dům v Praze Kunraticích dostává nový zateplovací systém. Stavbu děláme ve dvou lidech — dvoučlenný tým, který má každý detail pod kontrolou od první desky po poslední hmoždinku."
          covers={[
            { src: "/assets/gallery/aktualni-lead.webp", date: "22. 9. 2026" },
            { src: "/assets/gallery/aktualni-29.webp", date: "7. 10. 2026" },
          ]}
          photos={[
            { src: "/assets/gallery/aktualni-2.webp", date: "22. 9. 2026" },
            { src: "/assets/gallery/aktualni-3.webp", date: "22. 9. 2026" },
            { src: "/assets/gallery/aktualni-4.webp", date: "22. 9. 2026" },
            { src: "/assets/gallery/aktualni-5.webp", date: "23. 9. 2026" },
            { src: "/assets/gallery/aktualni-6.webp", date: "23. 9. 2026" },
            { src: "/assets/gallery/aktualni-8.webp", date: "23. 9. 2026" },
            { src: "/assets/gallery/aktualni-11.webp", date: "24. 9. 2026" },
            { src: "/assets/gallery/aktualni-12.webp", date: "25. 9. 2026" },
            { src: "/assets/gallery/aktualni-16.webp", date: "26. 9. 2026" },
            { src: "/assets/gallery/aktualni-17.webp", date: "29. 9. 2026" },
            { src: "/assets/gallery/aktualni-19.webp", date: "30. 9. 2026" },
            { src: "/assets/gallery/aktualni-21.webp", date: "1. 10. 2026" },
            { src: "/assets/gallery/aktualni-22.webp", date: "2. 10. 2026" },
            { src: "/assets/gallery/aktualni-23.webp", date: "3. 10. 2026" },
            { src: "/assets/gallery/aktualni-24.webp", date: "4. 10. 2026" },
            { src: "/assets/gallery/aktualni-25.webp", date: "6. 10. 2026" },
            { src: "/assets/gallery/aktualni-26.webp", date: "6. 10. 2026" },
            { src: "/assets/gallery/aktualni-27.webp", date: "7. 10. 2026" },
            { src: "/assets/gallery/aktualni-28.webp", date: "7. 10. 2026" },
          ]}
        />

        <Album
          testid="gallery-dum"
          title={
            <>
              Příběh jedné stavby — <span className="gold-text">rodinný dům</span>
            </>
          }
          desc="Reálná stavba RESTART PRO — jeden rodinný dům od hrubé stavby po dokončení. Žádné katalogové fotky, jen naše práce."
          covers={[
            { src: "/assets/gallery/final.webp" },
            { src: "/assets/gallery/etapa-1.webp" },
          ]}
          photos={[
            { src: "/assets/gallery/etapa-2.webp" },
            { src: "/assets/gallery/etapa-3.webp" },
            { src: "/assets/gallery/etapa-4.webp" },
          ]}
        />

        <Album
          testid="gallery-koupelna"
          title={
            <>
              Rekonstrukce koupelny — <span className="gold-text">před / po</span>
            </>
          }
          covers={[
            { src: "/assets/gallery/koupelna-pred.webp" },
            { src: "/assets/gallery/koupelna-1.webp" },
          ]}
          photos={[
            { src: "/assets/gallery/koupelna-2.webp" },
            { src: "/assets/gallery/koupelna-3.webp" },
            { src: "/assets/gallery/koupelna-4.webp" },
          ]}
        />

        <Album
          testid="gallery-sdk-luxfer"
          title={
            <>
              SDK příčka — <span className="gold-text">okno z luxfer</span>
            </>
          }
          covers={[
            { src: "/assets/gallery/sdk-final.webp" },
            { src: "/assets/gallery/sdk-1.webp" },
          ]}
          photos={[
            { src: "/assets/gallery/sdk-2.webp" },
            { src: "/assets/gallery/sdk-3.webp" },
            { src: "/assets/gallery/sdk-4.webp" },
          ]}
        />

        <Album
          testid="gallery-sdk-patro"
          title={
            <>
              SDK příčky — <span className="gold-text">patro rodinného domu</span>
            </>
          }
          covers={[
            { src: "/assets/gallery/pricky-lead.webp" },
            { src: "/assets/gallery/pricky-1.webp" },
          ]}
          photos={[
            { src: "/assets/gallery/pricky-2.webp" },
            { src: "/assets/gallery/pricky-3.webp" },
            { src: "/assets/gallery/pricky-4.webp" },
          ]}
        />

        <Album
          testid="gallery-topeni"
          title={
            <>
              <span className="gold-text">Podlahové topení</span>
            </>
          }
          covers={[
            { src: "/assets/gallery/topeni-lead.webp" },
            { src: "/assets/gallery/topeni-4.webp" },
          ]}
          photos={[
            { src: "/assets/gallery/topeni-3.webp" },
          ]}
        />

        <Album
          testid="gallery-krov"
          title={
            <>
              Krov a <span className="gold-text">střecha</span>
            </>
          }
          covers={[
            { src: "/assets/gallery/strecha-lead.webp" },
            { src: "/assets/gallery/strecha-2.webp" },
          ]}
          photos={[
            { src: "/assets/gallery/strecha-1.webp" },
            { src: "/assets/gallery/strecha-3.webp" },
            { src: "/assets/gallery/strecha-4.webp" },
          ]}
        />

        <Album
          testid="gallery-interier"
          title={
            <>
              Interiér <span className="gold-text">hrubé stavby</span>
            </>
          }
          covers={[
            { src: "/assets/gallery/interier-lead.webp" },
            { src: "/assets/gallery/interier-1.webp" },
          ]}
          photos={[
            { src: "/assets/gallery/interier-2.webp" },
            { src: "/assets/gallery/interier-3.webp" },
          ]}
        />

        <Album
          testid="gallery-pergola"
          title={
            <>
              Terasa a <span className="gold-text">pergola</span>
            </>
          }
          covers={[
            { src: "/assets/gallery/topeni-2.webp" },
            { src: "/assets/gallery/pergola-lead.webp" },
          ]}
          photos={[
            { src: "/assets/gallery/topeni-1.webp" },
            { src: "/assets/gallery/pergola-1.webp" },
            { src: "/assets/gallery/pergola-2.webp" },
            { src: "/assets/gallery/pergola-3.webp" },
            { src: "/assets/gallery/pergola-4.webp" },
          ]}
        />

        <Album
          testid="gallery-obklady"
          title={
            <>
              Obklady a <span className="gold-text">dlažba</span>
            </>
          }
          covers={[
            { src: "/assets/gallery/obklady-lead.webp" },
            { src: "/assets/gallery/obklady-1.webp" },
          ]}
          photos={[
            { src: "/assets/gallery/obklady-2.webp" },
            { src: "/assets/gallery/obklady-3.webp" },
            { src: "/assets/gallery/obklady-4.webp" },
          ]}
        />

        <Album
          testid="gallery-fitout"
          title={
            <>
              Komerční fit-outy — <span className="gold-text">Nanu-Nana, Sinsay, Ugo salaterie</span>
            </>
          }
          covers={[
            { src: "/assets/gallery/fitout-hotovo-lead.webp" },
            { src: "/assets/gallery/fitout-lead.webp" },
          ]}
          photos={[
            { src: "/assets/gallery/fitout-1.webp" },
            { src: "/assets/gallery/fitout-2.webp" },
            { src: "/assets/gallery/fitout-3.webp" },
            { src: "/assets/gallery/fitout-4.webp" },
            { src: "/assets/gallery/fitout-hotovo-1.webp" },
            { src: "/assets/gallery/fitout-hotovo-2.webp" },
            { src: "/assets/gallery/fitout2-1.webp" },
            { src: "/assets/gallery/fitout2-2.webp" },
            { src: "/assets/gallery/fitout2-3.webp" },
            { src: "/assets/gallery/fitout2-4.webp" },
          ]}
        />
      </div>
    </section>
  );
}
